import React, { useState, useEffect, useCallback } from 'react';
import {
  $BannerContainer,
  $ProgressBarHeader,
  $ProgressBarContainer,
  $ProgressBar,
  $ProgressBarFill,
  $NavigationButton,
  $CloseButton,
} from './PromotionBannerContainer.styled';
import Icon from '../Icon';

export interface PromotionBannerItem {
  id: string;
  component: React.ComponentType<any>;
  props: any;
  duration: number;
}

interface Props {
  banners: PromotionBannerItem[];
  onClose: () => void;
}

function PromotionBannerContainer({ banners, onClose }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [hasSeenAllBanners, setHasSeenAllBanners] = useState(false);
  const [seenBanners, setSeenBanners] = useState<Set<number>>(new Set([0]));

  const currentBanner = banners[currentIndex];
  const currentDuration = currentBanner?.duration || 15000;

  useEffect(() => {
    if (isPaused || !currentBanner) {
      return undefined;
    }

    const increment = (100 / currentDuration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;

        if (newProgress >= 100) {
          const nextIndex = (currentIndex + 1) % banners.length;
          setCurrentIndex(nextIndex);
          return 0;
        }

        return newProgress;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [currentIndex, isPaused, currentDuration, currentBanner, banners.length]);

  useEffect(() => {
    setSeenBanners(prev => new Set([...prev, currentIndex]));

    if (seenBanners.size >= banners.length) {
      setHasSeenAllBanners(true);
    }
  }, [currentIndex, banners.length, seenBanners.size]);

  useEffect(() => {
    setProgress(0);
  }, [currentIndex]);

  const navigateToBanner = useCallback(
    (index: number) => {
      if (index >= 0 && index < banners.length) {
        setCurrentIndex(index);
        setProgress(0);
        setSeenBanners(prev => new Set([...prev, index]));
      }
    },
    [banners.length],
  );

  const handlePrevious = () => {
    const prevIndex =
      currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    navigateToBanner(prevIndex);
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % banners.length;
    navigateToBanner(nextIndex);
  };

  const handleProgressBarClick = (index: number) => {
    navigateToBanner(index);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  if (!currentBanner || banners.length === 0) {
    return null;
  }

  const CurrentBannerComponent = currentBanner.component;
  const isLastBanner = currentIndex === banners.length - 1;
  const isFirstBanner = currentIndex === 0;
  const canShowCloseButton = hasSeenAllBanners && isLastBanner;

  const getProgressValue = (index: number) => {
    if (index < currentIndex) return 100;
    if (index === currentIndex) return progress;
    return 0;
  };

  return (
    <$BannerContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <$ProgressBarHeader>
        <$ProgressBarContainer>
          {banners.map((banner, index) => (
            <$ProgressBar
              key={banner.id}
              onClick={() => handleProgressBarClick(index)}
            >
              <$ProgressBarFill $progress={getProgressValue(index)} />
            </$ProgressBar>
          ))}
        </$ProgressBarContainer>

        <$NavigationButton
          onClick={handlePrevious}
          disabled={banners.length <= 1 || isFirstBanner}
        >
          <Icon name="ArrowBack" />
        </$NavigationButton>

        {canShowCloseButton ? (
          <$CloseButton onClick={onClose} data-cy="promotion-banner-close">
            <Icon name="Close" />
          </$CloseButton>
        ) : (
          <$NavigationButton
            onClick={handleNext}
            data-cy="promotion-banner-next"
            disabled={banners.length <= 1}
          >
            <Icon name="ArrowForward" />
          </$NavigationButton>
        )}
      </$ProgressBarHeader>

      <CurrentBannerComponent
        key={`${currentBanner.id}-${currentIndex}`}
        {...currentBanner.props}
      />
    </$BannerContainer>
  );
}

export default PromotionBannerContainer;
